// =============================================================================
// Sale-level (whole-invoice) discount — client-side allocation engine (PR D/D3a)
// =============================================================================
// The server RPC (post_sale_atomic_core, mig 274) TRUSTS the per-line net totals
// and VAT the client sends, and posts the whole-invoice discount as its own
// contra-revenue line from p_sale.sale_discount_amount. This module owns the one
// piece of math that must be provably correct: splitting a header discount across
// lines so the parts sum EXACTLY to the whole.
//
// The single shared primitive is `allocateProportional`. It is used at BOTH
// levels of the nested split:
//   1. header discount  → each sale line   (proportional to line net subtotal)
//   2. a bundle line's share → its components (proportional to component subtotal)
// Using one helper at both levels keeps the "Σ parts === whole" invariant identical
// everywhere. See saleDiscount.test.ts for the adversarial proofs.
// =============================================================================

/** Round to `minorUnit` decimal places (2 = Naira kobo). */
function roundTo(n: number, minorUnit: number): number {
  const f = 10 ** minorUnit
  return Math.round(n * f) / f
}

/**
 * Split `total` across `bases` proportionally to each base's share of the sum,
 * rounded to `minorUnit` dp, with the rounding remainder assigned to the entry
 * with the LARGEST base (first entry on a tie).
 *
 * INVARIANT (guaranteed for every input): the returned array sums EXACTLY to
 * roundTo(total, minorUnit). The remainder step absorbs all floating-point dust,
 * so callers can rely on the parts reconciling to the whole to the last kobo.
 *
 * Degenerate inputs are handled without NaN: an empty `bases` → []; a total of 0
 * → all zeros; a non-positive base sum (should never happen once the ≤-subtotal
 * guards run, but kept safe) → the whole rounded total lands on the first entry.
 * Negative bases are floored to 0 for the proportion (a transient negative line
 * while the user is typing never steals a share).
 */
export function allocateProportional(bases: number[], total: number, minorUnit = 2): number[] {
  const n = bases.length
  if (n === 0) return []

  const rounded = roundTo(total, minorUnit)
  const sumBase = bases.reduce((s, b) => s + Math.max(0, b), 0)

  const shares = sumBase <= 0
    ? new Array<number>(n).fill(0)
    : bases.map(b => roundTo((Math.max(0, b) / sumBase) * rounded, minorUnit))

  const allocated = shares.reduce((s, x) => s + x, 0)
  const remainder = roundTo(rounded - allocated, minorUnit)

  if (remainder !== 0) {
    let maxIdx = 0
    for (let i = 1; i < n; i++) {
      if (bases[i] > bases[maxIdx]) maxIdx = i
    }
    shares[maxIdx] = roundTo(shares[maxIdx] + remainder, minorUnit)
  }

  return shares
}

/**
 * Resolve the entered header-discount control into a ₦ amount.
 *   ₦ mode  → the entered value (rounded, floored at 0)
 *   % mode  → round(rawSubtotal * pct / 100) — the base is rawSubtotal (net of
 *             line discounts, GROSS of VAT + shipping), per the locked design.
 * No upper clamp: the ≤-subtotal / zero-subtotal rejection is the caller's guard
 * (client mirror of the mig-274 server guards), so this stays a pure resolver.
 */
export function resolveHeaderDiscountAmount(
  type: 'fixed' | 'percent',
  value: number,
  rawSubtotal: number,
  minorUnit = 2,
): number {
  if (!value || value <= 0) return 0
  const raw = type === 'percent' ? (rawSubtotal * value) / 100 : value
  return roundTo(Math.max(0, raw), minorUnit)
}

/**
 * Allocate a header discount across keyed sale lines, proportional to each line's
 * net subtotal. Returns a key → ₦ allocation map whose values sum EXACTLY to
 * roundTo(headerDiscount). A line absent from the map is treated as 0 by callers.
 */
export function allocateHeaderDiscount(
  lineBases: { key: string; base: number }[],
  headerDiscount: number,
  minorUnit = 2,
): Map<string, number> {
  const shares = allocateProportional(lineBases.map(l => l.base), headerDiscount, minorUnit)
  const map = new Map<string, number>()
  lineBases.forEach((l, i) => map.set(l.key, shares[i]))
  return map
}
